import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { T } from '../constants';

/**
 * Shared modal chrome for the Job Tracker, matching the app's existing dialog
 * language (see components/AppDialog.jsx): dimmed blurred backdrop, white card
 * with a 20px radius, spring entry, Escape to close, click-outside to close.
 */
export const JobModalShell = ({ title, subtitle, icon, accent = T.brand, onClose, children, footer, maxWidth = 560 }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // Stop the page behind the modal from scrolling while it is open.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const Icon = icon;

  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 99998,
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, fontFamily: T.font,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: T.surface, borderRadius: 20,
          width: '100%', maxWidth, maxHeight: 'calc(100vh - 32px)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          animation: 'jtDialogIn 0.18s cubic-bezier(.34,1.56,.64,1)',
          overflow: 'hidden',
        }}
      >
        <style>{`@keyframes jtDialogIn{from{opacity:0;transform:scale(0.94) translateY(8px)}to{opacity:1;transform:none}}`}</style>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '20px 22px 16px', borderBottom: `1px solid ${T.lineSoft}`, flexShrink: 0,
        }}>
          {Icon && (
            <div style={{
              width: 38, height: 38, borderRadius: 11, flexShrink: 0,
              background: `${accent}14`, border: `1px solid ${accent}28`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={18} strokeWidth={2.1} color={accent} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 800, color: T.ink, letterSpacing: -0.2 }}>{title}</h2>
            {subtitle && (
              <p style={{ margin: '3px 0 0', fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: T.faint, padding: 5, borderRadius: 8, lineHeight: 0, flexShrink: 0,
              transition: 'background 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = T.slate; e.currentTarget.style.color = T.body; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.faint; }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={{
            padding: '14px 22px', borderTop: `1px solid ${T.lineSoft}`,
            background: '#FCFCFD', display: 'flex', gap: 10, justifyContent: 'flex-end',
            flexWrap: 'wrap', flexShrink: 0,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Shared button styles ─────────────────────────────────────────────────── */

export const primaryBtnStyle = (disabled) => ({
  background: disabled ? '#E5E7EB' : T.brandGrad,
  color: disabled ? '#9CA3AF' : 'white',
  border: 'none', borderRadius: 10, padding: '10px 22px',
  fontSize: 13.5, fontWeight: 700, fontFamily: T.font,
  cursor: disabled ? 'not-allowed' : 'pointer',
  boxShadow: disabled ? 'none' : T.shadowBrand,
  display: 'inline-flex', alignItems: 'center', gap: 7,
  transition: 'transform 0.15s, box-shadow 0.15s',
});

export const secondaryBtnStyle = {
  background: T.surface, color: T.body,
  border: `1.5px solid ${T.line}`, borderRadius: 10, padding: '10px 20px',
  fontSize: 13.5, fontWeight: 600, fontFamily: T.font, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 7,
  transition: 'border-color 0.15s, background 0.15s',
};
